import pt from 'prop-types';
import React from 'react';
import { useLocale } from '../../../context/locale.context';
import { getMagazineArticleLink } from '../../../helpers/getMagazineArticleLink';
import { getPressReleaseLink } from '../../../helpers/getPressReleaseLink';
import {
  buildMailLink,
  buildTelLink,
  normalizeInternalLink,
} from '../../../helpers/link';
import { combine } from '../../../helpers/styles';
import Icon from '../icon';
import Text from '../text';
import * as styles from './styles.module.scss';
import { getDynamicPageLink } from '../../../helpers/getDynamicPageLink';

const Link = ({
  link,
  children,
  asButton,
  btnType,
  btnTransparent,
  allowIcon,
  className,
  download,
  mailto,
  tel,
  useGatsbyLink = true,
  gaId,
  ...props
}) => {
  const { locale } = useLocale();
  let internal,
    external,
    target = '_self';

  if (!link) return null;

  // INTERNAL
  if (typeof link.linkToPage === 'string') {
    internal = normalizeInternalLink(link.linkToPage);
  } else if (link.linkToPage?.internal?.type === 'ContentfulArticle') {
    internal = normalizeInternalLink(
      getMagazineArticleLink(link.linkToPage.slug)
    );
  } else if (link.linkToPage?.internal?.type === 'ContentfulPressRelease') {
    internal = normalizeInternalLink(getPressReleaseLink(link.linkToPage.slug));
  } else if (link.linkToPage) {
    internal = normalizeInternalLink(link.linkToPage.slug);
  }

  if (link.linkToDynamicPage) {
    internal = getDynamicPageLink(
      link.linkToDynamicPage,
      link.dynamicDetailPageName,
      link.dynamicDetailPageId
    );
  }

  if (internal) {
    if (link.queryParameter) {
      // always sort internal link query params so that they are always the same order
      const searchParams = new URLSearchParams(link.queryParameter);
      searchParams.sort();
      internal += `?${searchParams.toString()}`;
    }

    if (!internal.startsWith(`/${locale}`)) {
      internal = `/${locale}${internal}`;
    }

    if (link.anchor) {
      internal += `#${link.anchor}`;
    }
  }
  // END OF INTERNAL

  // EXTERNAL
  external = link.externalLink;

  if (link.downloadDocument) {
    external = link.downloadDocument?.document.file?.url;
  } else if (mailto) {
    external = buildMailLink(link.externalLink);
  } else if (tel) {
    external = buildTelLink(link.externalLink);
  } else if (external) {
    const hasProtocol = /\w+:\/\//.test(external);
    external = hasProtocol ? external : `https://${external}`;
    const pattern =
      /(http|https):\/\/(ifdesign.com|www.ifdesign.com)\/\w{2}\//gi;
    const re = new RegExp(pattern);
    if (re.test(external)) {
      external = external.replace(pattern, `/${locale}/`);
    }
  }

  if (external) {
    if (!external.startsWith('/')) {
      target = '_blank';
    }

    if (external.startsWith('/en') && locale != 'en') {
      external = external.replace('en', locale);
    }
  }
  // END OF EXTERNAL

  return (
    <a
      href={internal || external}
      target={target}
      rel={external ? 'external noopener noreferrer' : undefined}
      download={download}
      className={combine(
        asButton && styles.button,
        asButton && btnType && styles[btnType],
        btnTransparent && styles.transparent,
        styles.link,
        link.type && !btnType && styles[link.type],
        link.transparent && asButton && !btnType && styles.transparent,
        className
      )}
      id={link.gaId || gaId}
      {...props}
    >
      {asButton ? (
        <Text as="span" type="button">
          {children}
        </Text>
      ) : (
        children
      )}
      {allowIcon && link.icon && (
        <Icon name={link.icon} width={17} className={styles.ctaIcon} />
      )}
    </a>
  );
};

export const linkProptypes = {
  linkToPage: pt.oneOfType([pt.object, pt.string]),
  externalLink: pt.string,
  transparent: pt.bool,
  allowIcon: pt.bool,
  type: pt.oneOf(['primary', 'secondary', 'tertiary']),
  icon: pt.string,
  linkToDynamicPage: pt.oneOf([
    'Jury Filterlist',
    'Company Filterlist',
    'Project Filterlist',
    'Event Filterlist',
    'Company Detail',
    'Project Detail',
    'Jury Detail',
    'Event Detail',
  ]),
  dynamicDetailPageId: pt.number,
  dynamicDetailPageName: pt.string,
  queryParameter: pt.string,
};

Link.propTypes = {
  link: pt.shape(linkProptypes),
  children: pt.node.isRequired,
  className: pt.string,
  asButton: pt.bool,
  btnType: pt.string,
  btnTransparent: pt.bool,
  mailto: pt.bool,
  tel: pt.bool,
  useGatsbyLink: pt.bool,
};

export default Link;
